import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Edit, Plus, RefreshCw, Trash2, Server, Users, Shield, Settings } from 'lucide-react';
import StatusBadge from '../components/common/StatusBadge';
import DataTable from '../components/common/DataTable';
import type { Column } from '../components/common/DataTable';
import MTGEModal from '../components/mtge/MTGEModal';
import TenantAssignModal from '../components/mtge/TenantAssignModal';
import TenantEditModal from '../components/mtge/TenantEditModal';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { useToast } from '../components/common/ToastContainer';
import { SSHAccessSummary } from '../components/edges/SSHAccessSummary';
import mtgeService from '../services/mtge.service';
import type { MtgeDevice, MtgeTenantAssignment } from '../services/mtge.service';
import type { MtgeFormData } from '../components/mtge/MTGEModal';
import { observabilityService } from '../services/observability.service';
import { formatDate } from '../utils/formatDate';
import { useSettingsStore } from '../stores/settingsStore';
import { usePermissions } from '../hooks/usePermissions';

const MTGEDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [mtge, setMtge] = useState<MtgeDevice | null>(null);
  const [tenants, setTenants] = useState<MtgeTenantAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'tenants'>('overview');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [removeConfirm, setRemoveConfirm] = useState<{ show: boolean; tenant?: MtgeTenantAssignment }>({ show: false });
  const [removing, setRemoving] = useState(false);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [editingTenant, setEditingTenant] = useState<MtgeTenantAssignment | undefined>();
  const [isOnline, setIsOnline] = useState(false);

  const fetchMtge = async () => {
    if (!id) return;
    try {
      const data = await mtgeService.getById(id);
      setMtge(data);
    } catch (error: any) {
      toast.error('Load Failed', 'Failed to load shared gateway details');
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    if (!id) return;
    try {
      const data = await mtgeService.listTenants(id);
      setTenants(Array.isArray(data) ? data : []);
    } catch (error: any) {
      toast.error('Load Failed', 'Failed to load tenant assignments');
    }
  };

  useEffect(() => {
    fetchMtge();
    fetchTenants();
  }, [id]);

  // Check live online status
  useEffect(() => {
    if (!mtge) return;
    const checkOnline = async () => {
      try {
        const res = await observabilityService.getEdgesTelemetry();
        if (res.status === 'success' && res.data) {
          const serial = mtge.serial_number;
          const stats = res.data[serial] || res.data[serial.toLowerCase()];
          setIsOnline(!!(stats && (stats as any).cpu !== undefined));
        }
      } catch { /* telemetry unavailable */ }
    };
    checkOnline();
  }, [mtge]);

  const handleSaveMtge = async (data: MtgeFormData) => {
    if (!id) return;
    try {
      await mtgeService.update(id, {
        deviceName: data.device_name,
        maxTenants: data.max_tenants,
        wanIp: data.wan_ip || undefined,
        wanCidr: data.wan_cidr || '/24',
        wanGateway: data.wan_gateway || undefined,
        firmwareVersion: data.firmware_version || undefined,
      });
      toast.success('MTGE Updated', 'Shared gateway has been updated');
      await fetchMtge();
      setIsEditModalOpen(false);
    } catch (error: any) {
      toast.error('Update Failed', error.message || 'Failed to update MTGE');
      throw error;
    }
  };

  const handleAssignTenant = async (tenantId: string, internetBreakout: boolean, tunnelProtocol?: 'wireguard' | 'ikev2') => {
    if (!id) return;
    try {
      await mtgeService.assignTenant(id, { tenantId, internetBreakout, tunnelProtocol });
      toast.success('Tenant Assigned', 'Tenant has been assigned and onboard message sent');
      await Promise.all([fetchMtge(), fetchTenants()]);
    } catch (error: any) {
      toast.error('Assignment Failed', error.message || 'Failed to assign tenant');
      throw error;
    }
  };

  const handleRemoveTenant = async () => {
    if (!id || !removeConfirm.tenant) return;
    try {
      setRemoving(true);
      await mtgeService.removeTenant(id, removeConfirm.tenant.tenant_id);
      toast.success('Tenant Removed', 'Tenant has been offboarded from this gateway');
      setRemoveConfirm({ show: false });
      await Promise.all([fetchMtge(), fetchTenants()]);
    } catch (error: any) {
      toast.error('Remove Failed', error.message || 'Failed to remove tenant');
    } finally {
      setRemoving(false);
    }
  };

  const handleSyncTenant = async (tenantId: string) => {
    if (!id) return;
    try {
      setSyncing(tenantId);
      const result = await mtgeService.syncTenantConfig(id, tenantId);
      toast.success('Sync Initiated', `Config pushed with ${result.commandCount} commands (seq: ${result.seqNum})`);
      await fetchTenants();
    } catch (error: any) {
      toast.error('Sync Failed', error.message || 'Failed to sync tenant config');
    } finally {
      setSyncing(null);
    }
  };

  const getDisplayStatus = (): string => {
    if (!mtge) return 'Unknown';
    if (['pending_approval', 'approved', 'provisioned'].includes(mtge.status)) {
      const labels: Record<string, string> = { pending_approval: 'Pending', approved: 'Approved', provisioned: 'Provisioned' };
      return labels[mtge.status] || mtge.status;
    }
    return isOnline ? 'Online' : 'Offline';
  };

  const onboardStatusMap: Record<string, string> = {
    pending: 'Pending',
    provisioning: 'Provisioning',
    active: 'Active',
    error: 'Error',
    decommissioned: 'Decommissioned',
  };

  const syncStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      synced: 'bg-green-100 text-green-700',
      pending: 'bg-yellow-100 text-yellow-700',
      applying: 'bg-blue-100 text-blue-700',
      failed: 'bg-red-100 text-red-700',
      unknown: 'bg-gray-100 text-gray-600',
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[status] || colors.unknown}`}>
        {status === 'synced' ? 'Synced' : status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const tenantColumns: Column<MtgeTenantAssignment>[] = [
    {
      key: 'tenant_name',
      label: 'Tenant',
      sortable: true,
      render: (t) => <span className="font-medium">{t.tenant_name || t.tenant_id.substring(0, 8)}</span>,
    },
    {
      key: 'vrf_id',
      label: 'VRF',
      render: (t) => <span className="font-mono text-xs">{t.vrf_id}</span>,
    },
    {
      key: 'tenant_slot',
      label: 'Slot',
      render: (t) => <span>{t.tenant_slot}</span>,
    },
    {
      key: 'tunnel_protocol',
      label: 'Protocol',
      render: (t) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
          t.tunnel_protocol === 'ikev2'
            ? 'bg-purple-100 text-purple-700'
            : 'bg-blue-100 text-blue-700'
        }`}>
          {t.tunnel_protocol === 'ikev2' ? 'IKEv2' : 'WireGuard'}
        </span>
      ),
    },
    {
      key: 'tunnel_mode',
      label: 'Tunnel Mode',
      render: (t) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
          t.tunnel_mode === 'full' ? 'bg-orange-100 text-orange-700' : 'bg-cyan-100 text-cyan-700'
        }`}>
          {t.tunnel_mode === 'full' ? 'Full' : 'Split'}
        </span>
      ),
    },
    {
      key: 'wg0_port',
      label: 'Ports (IoT/Apps/Peer)',
      render: (t) => <span className="font-mono text-xs">{t.wg0_port}/{t.wg1_port}/{t.wg2_port}</span>,
    },
    {
      key: 'internet_breakout',
      label: 'Internet',
      render: (t) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
          t.internet_breakout === 1
            ? 'bg-green-100 text-green-700'
            : 'bg-gray-100 text-gray-600'
        }`}>
          {t.internet_breakout === 1 ? 'Enabled' : 'Disabled'}
        </span>
      ),
    },
    {
      key: 'onboard_status',
      label: 'Onboard',
      render: (t) => <StatusBadge status={onboardStatusMap[t.onboard_status] || t.onboard_status} />,
    },
    {
      key: 'sync_status',
      label: 'Config',
      render: (t) => syncStatusBadge(t.sync_status),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (t) => (
        <div className="flex items-center justify-end space-x-2">
          {canUpdate && (
            <button
              onClick={() => setEditingTenant(t)}
              className="p-1.5 hover:bg-gray-100 rounded transition-colors"
              title="Settings"
            >
              <Settings className="w-4 h-4 text-gray-600 hover:text-primary-600" />
            </button>
          )}
          {canUpdate && (
            <button
              onClick={() => handleSyncTenant(t.tenant_id)}
              disabled={syncing === t.tenant_id}
              className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded transition-colors disabled:opacity-50"
              title="Sync Config"
            >
              {syncing === t.tenant_id ? 'Syncing...' : 'Sync'}
            </button>
          )}
          {canDelete && (
            <button
              onClick={() => setRemoveConfirm({ show: true, tenant: t })}
              className="p-1.5 hover:bg-red-50 rounded transition-colors"
              title="Remove Tenant"
            >
              <Trash2 className="w-4 h-4 text-gray-600 hover:text-red-600" />
            </button>
          )}
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!mtge) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Shared gateway not found</p>
        <button onClick={() => navigate('/mtges')} className="btn-primary mt-4">Back to List</button>
      </div>
    );
  }

  const dateSettings = useSettingsStore.getState().getDateFormatSettings();
  const capacityPercent = mtge.max_tenants > 0 ? Math.round((mtge.current_tenants / mtge.max_tenants) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button onClick={() => navigate('/mtges')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center space-x-3">
              <h1 className="text-3xl font-bold text-gray-900">{mtge.device_name}</h1>
              <StatusBadge status={getDisplayStatus()} />
            </div>
            <p className="text-gray-600 mt-1">Serial: {mtge.serial_number}</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button onClick={() => { fetchMtge(); fetchTenants(); }} className="btn-secondary inline-flex items-center space-x-2">
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
          {(mtge.status === 'provisioned' || mtge.status === 'active') && (
            <button onClick={() => navigate(`/mtges/${id}/config`)} className="btn-secondary inline-flex items-center space-x-2">
              <Settings className="w-4 h-4" />
              <span>Configure</span>
            </button>
          )}
          {canUpdate && (
            <button onClick={() => setIsEditModalOpen(true)} className="btn-primary inline-flex items-center space-x-2">
              <Edit className="w-4 h-4" />
              <span>Edit</span>
            </button>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'overview'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('tenants')}
            className={`py-4 px-1 border-b-2 font-medium text-sm inline-flex items-center space-x-2 ${
              activeTab === 'tenants'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <span>Tenants</span>
            <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full text-xs">{tenants.length}</span>
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Device Info */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
              <Server className="w-5 h-5 text-primary-600" />
              <span>Device Information</span>
            </h3>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Serial Number</dt>
                <dd className="text-sm font-medium font-mono">{mtge.serial_number}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">WAN IP</dt>
                <dd className="text-sm font-medium">
                  {mtge.wan_ip || '-'}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">WAN Gateway</dt>
                <dd className="text-sm font-medium">
                  {mtge.wan_ip
                    ? (mtge.wan_gateway || `${mtge.wan_ip.split('.').slice(0, 3).join('.')}.1`)
                    : '-'}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Firmware</dt>
                <dd className="text-sm font-medium">{mtge.firmware_version || '-'}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Agent Version</dt>
                <dd className="text-sm font-medium font-mono">{mtge.agent_version || '-'}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Base Tunnel Port</dt>
                <dd className="text-sm font-medium">{mtge.base_wg_port}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Registered</dt>
                <dd className="text-sm font-medium">{mtge.registered_at ? formatDate(mtge.registered_at, dateSettings) : '-'}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Last Seen</dt>
                <dd className="text-sm font-medium">{mtge.last_seen ? formatDate(mtge.last_seen, dateSettings) : '-'}</dd>
              </div>
            </dl>
          </div>

          {/* Capacity */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
              <Users className="w-5 h-5 text-primary-600" />
              <span>Capacity</span>
            </h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-500">Tenant Capacity</span>
                  <span className="font-medium">{mtge.current_tenants} / {mtge.max_tenants}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full transition-all ${
                      capacityPercent >= 90 ? 'bg-red-500' : capacityPercent >= 70 ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${capacityPercent}%` }}
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 pt-2">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">{mtge.current_tenants}</div>
                  <div className="text-xs text-gray-500">Active</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">{mtge.max_tenants - mtge.current_tenants}</div>
                  <div className="text-xs text-gray-500">Available</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">{mtge.max_tenants}</div>
                  <div className="text-xs text-gray-500">Maximum</div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Tenant Summary */}
          {tenants.length > 0 && (
            <div className="card p-6 lg:col-span-2">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                <Shield className="w-5 h-5 text-primary-600" />
                <span>Tenant Summary</span>
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-green-700">{tenants.filter(t => t.onboard_status === 'active').length}</div>
                  <div className="text-xs text-green-600">Active</div>
                </div>
                <div className="bg-yellow-50 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-yellow-700">{tenants.filter(t => t.onboard_status === 'pending' || t.onboard_status === 'provisioning').length}</div>
                  <div className="text-xs text-yellow-600">Pending</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-blue-700">{tenants.filter(t => t.sync_status === 'synced').length}</div>
                  <div className="text-xs text-blue-600">Synced</div>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-red-700">{tenants.filter(t => t.sync_status === 'failed' || t.onboard_status === 'error').length}</div>
                  <div className="text-xs text-red-600">Issues</div>
                </div>
              </div>
            </div>
          )}

          {/* SSH Access Status */}
          {(mtge.status === 'provisioned' || mtge.status === 'active') && (
            <div className="lg:col-span-2">
              <SSHAccessSummary edgeGuid={mtge.serial_number} />
            </div>
          )}
        </div>
      )}

      {activeTab === 'tenants' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Tenant Assignments</h3>
            {canCreate && (
              <button
                onClick={() => setIsAssignModalOpen(true)}
                disabled={mtge.current_tenants >= mtge.max_tenants}
                className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50"
              >
                <Plus className="w-4 h-4" />
                <span>Assign Tenant</span>
              </button>
            )}
          </div>
          <div className="card">
            <DataTable
              columns={tenantColumns}
              data={tenants}
              searchPlaceholder="Search tenants..."
              loading={false}
            />
          </div>
        </div>
      )}

      {/* Modals */}
      <MTGEModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleSaveMtge}
        mtge={{
          id: mtge.id,
          device_name: mtge.device_name,
          max_tenants: mtge.max_tenants,
          base_wg_port: mtge.base_wg_port,
          wan_ip: mtge.wan_ip || '',
          wan_cidr: mtge.wan_cidr || '/24',
          wan_gateway: mtge.wan_gateway || '',
          firmware_version: mtge.firmware_version || '',
        }}
        mode="edit"
      />
      <TenantAssignModal
        isOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        onAssign={handleAssignTenant}
        existingTenantIds={tenants.map(t => t.tenant_id)}
      />
      <TenantEditModal
        isOpen={!!editingTenant}
        onClose={() => setEditingTenant(undefined)}
        tenant={editingTenant}
        onSave={async (data) => {
          try {
            await mtgeService.updateTenantAssignment(id!, editingTenant!.tenant_id, data);
            toast.success('Settings Updated', 'Tenant settings have been updated');
            await fetchTenants();
            setEditingTenant(undefined);
          } catch (error: any) {
            toast.error('Update Failed', error.message || 'Failed to update tenant settings');
          }
        }}
      />
      <ConfirmDialog
        isOpen={removeConfirm.show}
        title="Remove Tenant"
        message={`Are you sure you want to remove "${removeConfirm.tenant?.tenant_name || removeConfirm.tenant?.tenant_id}" from this gateway? The tenant's VRF and tunnel interfaces will be torn down.`}
        confirmText="Remove"
        variant="danger"
        onConfirm={handleRemoveTenant}
        onClose={() => setRemoveConfirm({ show: false })}
        isLoading={removing}
      />
    </div>
  );
};

export default MTGEDetail;
